import React, { useState, useMemo } from 'react';

interface ActivitySlot {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  instructorId: number;
  instructorName: string;
  topicMasterId: number;
  topicName: string;
  agenda: string;
  batchIds?: number[];
  attendanceTaken?: boolean;
  roomId?: number;
  roomName?: string;
  centreId?: number;
  centreName?: string;
}

interface BatchMapping {
  batchId: number;
  batchName: string;
  batchCode?: string;
}

interface TemplateMonthViewProps {
  slots: ActivitySlot[];
  currentMonth: Date;
  templateStartDate: string;
  templateEndDate: string;
  isEditMode: boolean;
  onMonthChange: (month: Date) => void;
  onDayClick: (date: string) => void;
  onAddSlot: (date?: string) => void;
  onEditSlot: (slot: ActivitySlot) => void;
  onDeleteSlot: (slotId: number) => void;
  templateBatchIds?: number[];
  batchMappings?: BatchMapping[];
  onAttendance?: (batchIds: number[], templateSlotId: number, instructorId?: number, instructorName?: string) => void;
  onAttendanceNode?: (batchIds: number[], templateSlotId: number, instructorId?: number, instructorName?: string) => void;
}

function parseLocalDate(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatTimeShort(time: string): string {
  if (!time) return '';
  const parts = time.split(':');
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === '00' ? `${h12}${ampm}` : `${h12}:${m}${ampm}`;
}

function formatTimeDisplay(time: string): string {
  if (!time) return '-';
  const parts = time.split(':');
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
}

const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const TemplateMonthView: React.FC<TemplateMonthViewProps> = ({
  slots,
  currentMonth,
  templateStartDate,
  templateEndDate,
  isEditMode,
  onMonthChange,
  onDayClick,
  onAddSlot,
  onEditSlot,
  onDeleteSlot,
  templateBatchIds = [],
  batchMappings = [],
  onAttendance,
  onAttendanceNode,
}) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const batchNameMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const b of batchMappings) {
      map.set(b.batchId, b.batchCode || b.batchName || `Batch ${b.batchId}`);
    }
    return map;
  }, [batchMappings]);

  const getBatchNames = (slot: ActivitySlot): string[] => {
    const ids = (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds;
    return ids.map(id => batchNameMap.get(id) || `Batch ${id}`);
  };
  const [selectedWeekIndex, setSelectedWeekIndex] = useState<number | null>(null);

  // Build slot-by-date map
  const slotsByDate = useMemo(() => {
    const map = new Map<string, ActivitySlot[]>();
    for (const slot of slots) {
      if (!slot.date) continue;
      const existing = map.get(slot.date) || [];
      existing.push(slot);
      map.set(slot.date, existing);
    }
    // Sort each day's slots by startTime
    map.forEach((daySlots) => {
      daySlots.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    });
    return map;
  }, [slots]);

  // Build calendar weeks grid
  const weeks = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Day of week (0=Mon, 6=Sun) for the 1st
    let startDow = firstDay.getDay() - 1;
    if (startDow < 0) startDow = 6; // Sunday

    const result: (Date | null)[][] = [];
    let currentWeek: (Date | null)[] = [];

    // Pad start
    for (let i = 0; i < startDow; i++) {
      currentWeek.push(null);
    }

    for (let d = 1; d <= lastDay.getDate(); d++) {
      currentWeek.push(new Date(year, month, d));
      if (currentWeek.length === 7) {
        result.push(currentWeek);
        currentWeek = [];
      }
    }

    // Pad end
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      result.push(currentWeek);
    }

    return result;
  }, [currentMonth]);

  const tStart = parseLocalDate(templateStartDate);
  const tEnd = parseLocalDate(templateEndDate);
  const today = new Date();
  const todayKey = toDateKey(today);

  const canGoPrev = () => {
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const prevMonthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0);
    return prevMonthEnd >= tStart;
  };

  const canGoNext = () => {
    const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
    return nextMonth <= tEnd;
  };

  const handlePrevMonth = () => {
    if (!canGoPrev()) return;
    onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    setSelectedDate(null);
    setSelectedWeekIndex(null);
  };

  const handleNextMonth = () => {
    if (!canGoNext()) return;
    onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    setSelectedDate(null);
    setSelectedWeekIndex(null);
  };

  const handleDayCellClick = (dateKey: string, weekIndex: number) => {
    if (selectedDate === dateKey) {
      // Toggle off
      setSelectedDate(null);
      setSelectedWeekIndex(null);
    } else {
      setSelectedDate(dateKey);
      setSelectedWeekIndex(weekIndex);
    }
  };

  const isInRange = (d: Date): boolean => {
    return d >= tStart && d <= tEnd;
  };

  const renderDayDetailStrip = () => {
    if (!selectedDate) return null;

    const daySlots = slotsByDate.get(selectedDate) || [];
    const dateObj = parseLocalDate(selectedDate);
    const dateLabel = dateObj.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    return (
      <div className="day-detail-strip">
        <div className="strip-header">
          <div className="strip-date-info">
            <span className="strip-date">{dateLabel}</span>
            <span className="strip-count">{daySlots.length} slot{daySlots.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="strip-actions">
            <button
              className="strip-day-view-btn"
              onClick={() => onDayClick(selectedDate)}
            >
              Open Day View
            </button>
            <button
              className="strip-close-btn"
              onClick={() => { setSelectedDate(null); setSelectedWeekIndex(null); }}
            >
              &times;
            </button>
          </div>
        </div>

        {daySlots.length === 0 ? (
          <div className="strip-empty">
            No slots on this date.
            {isEditMode && (
              <button className="strip-add-btn" onClick={() => onAddSlot(selectedDate)}>
                + Add Slot
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="strip-slots">
              {daySlots.map(slot => (
                <div key={slot.id} className="strip-slot-card">
                  <div className="strip-slot-time">
                    {formatTimeDisplay(slot.startTime)} - {formatTimeDisplay(slot.endTime)}
                    {slot.attendanceTaken && (
                      <span className="attendance-done-badge" title="Attendance taken">A</span>
                    )}
                  </div>
                  <div className="strip-slot-details">
                    {slot.instructorName && (
                      <span className="strip-slot-instructor">{slot.instructorName}</span>
                    )}
                    {slot.topicName && (
                      <span className="strip-slot-topic">{slot.topicName}</span>
                    )}
                    {(slot.roomName || slot.centreName) && (
                      <span className="strip-slot-location">
                        {slot.roomName && <span>🏫 {slot.roomName}</span>}
                        {slot.centreName && <span>📍 {slot.centreName}</span>}
                      </span>
                    )}
                    {batchMappings.length > 0 && (
                      <span className="strip-slot-batches">
                        {getBatchNames(slot).map((name, idx) => (
                          <span key={idx} className="slot-batch-tag">{name}</span>
                        ))}
                      </span>
                    )}
                    {slot.agenda && (
                      <span className="strip-slot-agenda">{slot.agenda}</span>
                    )}
                  </div>
                  <div className="strip-slot-actions">
                    {/* Java attendance button hidden — Node attendance is now the canonical "Attendance" button.
                        Functionality preserved via onAttendance callback; only the button is hidden. */}
                    {/* {onAttendance && ((slot.batchIds && slot.batchIds.length > 0) || templateBatchIds.length > 0) && (
                      <button className="strip-attendance-btn" title="Take Attendance (Java)" onClick={() => onAttendance(
                        (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds,
                        slot.id,
                        slot.instructorId,
                        slot.instructorName
                      )}>Attendance</button>
                    )} */}
                    {onAttendanceNode && ((slot.batchIds && slot.batchIds.length > 0) || templateBatchIds.length > 0) && (
                      <button
                        className="strip-attendance-btn"
                        title="Take Attendance"
                        style={{ background: 'var(--brand-primary)', color: 'var(--text-on-brand)', borderColor: 'var(--brand-primary)' }}
                        onClick={() => onAttendanceNode(
                          (slot.batchIds && slot.batchIds.length > 0) ? slot.batchIds : templateBatchIds,
                          slot.id,
                          slot.instructorId,
                          slot.instructorName
                        )}
                      >
                        Attendance
                      </button>
                    )}
                    {isEditMode && !slot.attendanceTaken && (
                      <>
                        <button className="strip-edit-btn" onClick={() => onEditSlot(slot)}>Edit</button>
                        <button className="strip-delete-btn" onClick={() => onDeleteSlot(slot.id)}>Delete</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {isEditMode && (
              <button className="strip-add-btn" onClick={() => onAddSlot(selectedDate)}>
                + Add Slot
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="template-month-view">
      {/* Month header */}
      <div className="month-header">
        <button
          className="month-nav-btn"
          onClick={handlePrevMonth}
          disabled={!canGoPrev()}
        >
          &lsaquo;
        </button>
        <span className="month-title">
          {MONTH_NAMES[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button
          className="month-nav-btn"
          onClick={handleNextMonth}
          disabled={!canGoNext()}
        >
          &rsaquo;
        </button>
      </div>

      {/* Weekday headers */}
      <div className="month-weekday-headers">
        {WEEKDAY_HEADERS.map(day => (
          <div key={day} className="weekday-header-cell">{day}</div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="month-grid-body">
        {weeks.map((week, weekIndex) => (
          <div className="month-week-row" key={weekIndex}>
            {week.map((day, dayIndex) => {
              if (!day) {
                return <div key={dayIndex} className="month-day-cell empty" />;
              }

              const dateKey = toDateKey(day);
              const inRange = isInRange(day);
              const isToday = dateKey === todayKey;
              const daySlots = slotsByDate.get(dateKey) || [];
              const hasSlots = daySlots.length > 0;
              const isSelected = selectedDate === dateKey;
              const allAttendanceTaken = hasSlots && daySlots.every(s => s.attendanceTaken);

              const classes = [
                'month-day-cell',
                !inRange && 'outside-range',
                isToday && 'today',
                hasSlots && 'has-slots',
                isSelected && 'selected',
              ].filter(Boolean).join(' ');

              const clickable = inRange || hasSlots;
              return (
                <div
                  key={dayIndex}
                  className={classes}
                  onClick={clickable ? () => handleDayCellClick(dateKey, weekIndex) : undefined}
                  style={clickable ? undefined : { cursor: 'default' }}
                >
                  <div className="day-cell-header">
                    <span className="day-number">{day.getDate()}</span>
                    {hasSlots && (
                      <span className={`slot-count-badge ${allAttendanceTaken ? 'attendance-taken' : ''}`}>{daySlots.length}</span>
                    )}
                  </div>
                  {hasSlots && (
                    <div className="day-slot-previews">
                      {daySlots.slice(0, 3).map((s, idx) => (
                        <div key={idx} className={`day-slot-preview ${s.attendanceTaken ? 'attendance-done' : ''}`}>
                          <span className="preview-time">{formatTimeShort(s.startTime)}</span>
                          <span className="preview-text">
                            {s.topicName || s.instructorName || s.agenda || 'Slot'}
                          </span>
                        </div>
                      ))}
                      {daySlots.length > 3 && (
                        <div className="day-slot-more">+{daySlots.length - 3} more</div>
                      )}
                    </div>
                  )}
                  {isEditMode && inRange && !hasSlots && (
                    <button
                      className="day-add-btn"
                      onClick={(e) => { e.stopPropagation(); onAddSlot(dateKey); }}
                    >
                      +
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Day detail strip — rendered once at the bottom of the calendar to avoid grid/flex layout quirks */}
      {selectedDate && renderDayDetailStrip()}
    </div>
  );
};

export default TemplateMonthView;
